import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { CheckCircle, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import confetti from 'canvas-confetti';

export const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  
  // Parâmetros retornados pelo Mercado Pago
  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');
  const externalReference = searchParams.get('external_reference');

  useEffect(() => {
    // Animação de confetti ao carregar
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Revalidar auth para pegar has_active_subscription atualizado.
    // Poll até o webhook processar (máx ~15s).
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      await checkAuth();
      if (attempts < 5) {
        setTimeout(poll, 3000);
      }
    };
    poll();
  }, [checkAuth]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-slate-900">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-emerald-500/30 p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-4" data-testid="success-title">
            Assinatura Confirmada!
          </h1>

          {/* Message */}
          <p className="text-slate-300 text-lg mb-6">
            Parabéns! Sua assinatura foi ativada com sucesso. 
            Agora você tem acesso a todos os recursos do seu plano.
          </p>

          {/* Payment Details */}
          {paymentId && (
            <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-white font-medium mb-2">Detalhes do Pagamento</h3>
              <div className="space-y-1 text-sm">
                <p className="text-slate-400">
                  ID do Pagamento: <span className="text-white font-mono">{paymentId}</span>
                </p>
                <p className="text-slate-400">
                  Status: <span className="text-emerald-400 font-medium">{status || 'approved'}</span>
                </p>
              </div>
            </div>
          )}

          {/* Credits Info */}
          <div className="flex items-center justify-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
            <Zap className="w-5 h-5 text-amber-400" />
            <span className="text-amber-200">
              Seus créditos já estão disponíveis para uso!
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/seller/dashboard')}
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90"
              data-testid="go-to-dashboard-btn"
            >
              <span className="flex items-center gap-2">
                Ir para o Painel
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
            <Button
              onClick={() => navigate('/seller/products')}
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Ver Meus Produtos
            </Button>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mt-8 bg-slate-800/30 rounded-xl p-6 border border-slate-700">
          <h3 className="text-white font-medium mb-4">Próximos Passos</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">1</span>
              <span className="text-slate-300">Configure seu WhatsApp IA para automatizar vendas</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">2</span>
              <span className="text-slate-300">Adicione seus produtos ao marketplace</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">3</span>
              <span className="text-slate-300">Compartilhe seu Store ID com clientes</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
